import { getAccessToken } from './getAccessToken';
import { ENVIRONMENT_URL, DATAVERSE_TABLES } from '../constants/dataverse';

const environmentUrl = ENVIRONMENT_URL;
import moment from 'moment';


// Interface for Code Verification data
interface CodeVerificationData {
  cr3ea_qualitytourid: string;
  cr3ea_title: string;
  cr3ea_cycle: string;
  cr3ea_shift: string;
  cr3ea_tourstartdate: string;
  cr3ea_observedby: string;
  cr3ea_remarks: string | null;
  cr3ea_sku: string | null;
  /** ISO yyyy-mm-dd when set */
  cr3ea_pkddate?: string | null;
  cr3ea_expirydate?: string | null;
  cr3ea_batchno?: string | null;
  cr3ea_productname: string;
  cr3ea_executivename: string;
  cr3ea_attachments?: string | null;
  cr3ea_platform?: string;
}

interface SaveResponse {
  success: boolean;
  message: string;
  data?: any;
}

/** Attachment rows from UI / file service (SharePoint URL or offline blob preview). */
export type CodeVerificationAttachmentFile = {
  fileName: string;
  fileUrl: string;
  isOffline: boolean;
};

/** JSON array stored in cr3ea_attachments for links + offline file names. */
export function serializeCodeVerificationAttachments(
  files: CodeVerificationAttachmentFile[] | undefined
): string | null {
  if (!files?.length) return null;
  const items: { name: string; url: string | null }[] = [];
  for (const f of files) {
    const name = (f.fileName || 'file').trim() || 'file';
    if (f.fileUrl && /^https?:\/\//i.test(f.fileUrl)) {
      items.push({ name, url: f.fileUrl });
    } else {
      items.push({ name, url: null });
    }
  }
  if (items.length === 0) return null;
  return JSON.stringify(items);
}

// API for start section handler
export async function startSessionHandler(
  cycleNum: number,
  product: string,
  executiveName: string
) {
  console.log("Start Session handler called for cycle:", cycleNum);
  
  const startData = {
    product,
    executiveName
  };

  localStorage.setItem(`cycle-${cycleNum}-start-data`, JSON.stringify(startData));
  console.log("Start data saved to localStorage:", startData);
  
  return startData;
}

// API for fetch cycle data
export async function fetchCycleData(QualityTourId: string) {
  try {
    const tokenResult = await getAccessToken();
    const accessToken = tokenResult?.token;
    if (!accessToken) {
      throw new Error("Access token is invalid or missing");
    }

    const headers = {
      "Accept": "application/json",
      "OData-MaxVersion": "4.0",
      "OData-Version": "4.0",
      "Authorization": `Bearer ${accessToken}`
    };

    const apiVersion = "9.2";
    const tableName = DATAVERSE_TABLES.CODE_VERIFICATION;
    // const tableName = "cr3ea_codeverifications";
    const selectBase =
      'cr3ea_productname,cr3ea_executivename,cr3ea_sku,cr3ea_remarks,cr3ea_cycle,cr3ea_attachments,' +
      'cr3ea_pkddate,cr3ea_expirydate,cr3ea_batchno';
    let apiUrl = `${environmentUrl}/api/data/v${apiVersion}/${tableName}?$filter=cr3ea_qualitytourid eq '${QualityTourId}'&$select=${selectBase}`;

    let response = await fetch(apiUrl, { headers });
    if (!response.ok) {
      let errText = await response.text();
      const tryFallback =
        response.status === 400 && /pkddate|expirydate|batchno|property/i.test(errText);
      if (tryFallback) {
        const fallbackSelect =
          'cr3ea_productname,cr3ea_executivename,cr3ea_sku,cr3ea_remarks,cr3ea_cycle,cr3ea_attachments';
        apiUrl = `${environmentUrl}/api/data/v${apiVersion}/${tableName}?$filter=cr3ea_qualitytourid eq '${QualityTourId}'&$select=${fallbackSelect}`;
        response = await fetch(apiUrl, { headers });
        if (!response.ok) errText = await response.text();
      }
      if (!response.ok) throw new Error(`Failed to fetch data: ${response.status} - ${errText}`);
    }

    const data = await response.json();
    // Return one entry per record (per product) so multiple products per cycle are preserved
    const result: any[] = [];

    data.value.forEach((record: any) => {
      const cycleNum = record.cr3ea_cycle.replace('Cycle-', '');
      result.push({
        cycleNum,
        product: record.cr3ea_productname,
        executiveName: record.cr3ea_executivename,
        sku: record.cr3ea_sku,
        remarks: record.cr3ea_remarks,
        attachments: record.cr3ea_attachments || null,
        pkdDate: record.cr3ea_pkddate ?? null,
        expiryDate: record.cr3ea_expirydate ?? null,
        batchNo: record.cr3ea_batchno ?? null,
      });
    });

    return result;
  } catch (error) {
    console.error('Error fetching cycle data:', error);
    return [];
  }
}

// API for setting up the cycle data
export async function collectEstimationDataCycleSave(
  cycleNum: number,
  formData: any,
  QualityTourId: string,
  UserName: string,
  attachmentFiles?: CodeVerificationAttachmentFile[]
) {
  console.log('=== COLLECTING CODE VERIFICATION DATA ===');
  console.log('Cycle number:', cycleNum);
  console.log('Form data:', formData);
  console.log('Quality Tour ID:', QualityTourId);
  console.log('User name:', UserName);

  const savedData: CodeVerificationData[] = [];
  
  // Get start data from localStorage
  const startDataStr = localStorage.getItem(`cycle-${cycleNum}-start-data`);
  const startData = startDataStr ? JSON.parse(startDataStr) : {};
  const product = startData.product || "N/A";
  const executiveName = startData.executiveName || "N/A";

  const pkd = (formData.pkdDate || '').trim();
  const exp = (formData.expiryDate || '').trim();
  const batch = (formData.batchNo || '').trim();

  const fromSync = formData.attachments;
  const attachmentsSerialized =
    typeof fromSync === 'string' && fromSync.trim() !== ''
      ? fromSync.trim()
      : serializeCodeVerificationAttachments(attachmentFiles);

  const data: CodeVerificationData = {
    "cr3ea_qualitytourid": QualityTourId,
    "cr3ea_title": 'CodeVerification_' + moment().format('DD/MM/YYYY'),
    "cr3ea_cycle": `Cycle-${cycleNum}`,
    "cr3ea_shift": sessionStorage.getItem("shiftValue") || "shift 1",
    "cr3ea_tourstartdate": moment().format('DD/MM/YYYY'),
    "cr3ea_observedby": UserName || '',
    "cr3ea_remarks": formData.majorDefectsRemarks || null,
    "cr3ea_sku": formData.sku || null,
    "cr3ea_pkddate": pkd ? pkd : null,
    "cr3ea_expirydate": exp ? exp : null,
    "cr3ea_batchno": batch ? batch : null,
    "cr3ea_productname": product,
    "cr3ea_executivename": executiveName,
    "cr3ea_attachments": attachmentsSerialized,
    "cr3ea_platform": "PWA",
  };

  savedData.push(data);
  console.log('Collected data for API:', savedData);
  
  return { savedData };
}

// API for save section
export async function saveSectionApiCall(data: CodeVerificationData[]): Promise<SaveResponse> {
  try {
    console.log('=== SAVING CODE VERIFICATION DATA ===');
    console.log('Data to save:', data);
    
    const tokenResult = await getAccessToken();
    const accessToken = tokenResult?.token;
    if (!accessToken) {
      throw new Error("Access token is invalid or missing");
    }

    const headers = {
      "Accept": "application/json",
      "Content-Type": "application/json; charset=utf-8",
      "OData-MaxVersion": "4.0",
      "OData-Version": "4.0",
      "Prefer": "return=representation",
      "Authorization": `Bearer ${accessToken}`
    };

    const apiVersion = "9.2";
    const tableName = DATAVERSE_TABLES.CODE_VERIFICATION;
    // const tableName = "cr3ea_codeverifications";
    const apiUrl = `${environmentUrl}/api/data/v${apiVersion}/${tableName}`;

    const results = [];
    for (const record of data) {
      console.log('Saving record:', record);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(record)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to save record:', response.status, errorText);
        throw new Error(`Failed to save record: ${response.status} - ${errorText}`);
      }
      
      const result = await response.json();
      console.log('Record saved successfully:', result);
      results.push(result);
    }

    console.log(`${data.length} records saved successfully`);
    return {
      success: true,
      message: `${data.length} records saved successfully`,
      data: results
    };
  } catch (error) {
    console.error('Error saving code verification records:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to save records. Please try again.',
      data: []
    };
  }
}

// Interface for the cycle data structure
export interface CodeVerificationCycleData {
  cycleNum: string;
  product: string;
  executiveName: string;
  sku: string | null;
  remarks: string | null;
  attachments?: string | null;
  /** ISO yyyy-mm-dd from date input */
  pkdDate?: string | null;
  expiryDate?: string | null;
  batchNo?: string | null;
}